import { Device, Group } from "@fairfleet/geotab";
import { endOfDay, isValid, startOfDay } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { endOfCalendarDayInZone, startOfCalendarDayInZone } from "../utils/timeZone";
import { usePersistedDateRange } from "@/hooks/userState/usePersistedDateRange";

/**
 * Turns a calendar day picked in the browser (a local-midnight `Date`) into the instants that
 * bound that calendar day in the audit's time zone.
 */
function dayBounds(day: Date, timeZone: string) {
  const year = day.getFullYear();
  const month = day.getMonth() + 1;
  const date = day.getDate();
  return {
    start: startOfCalendarDayInZone(year, month, date, timeZone),
    end: endOfCalendarDayInZone(year, month, date, timeZone),
  };
}

/**
 * Filter state of the audit page. The picked days are calendar days; the query instants
 * derive from them in the audit's time zone. The date range is remembered per user as a rolling
 * lookback (see `usePersistedDateRange`), the rest resets.
 */
export function useAuditFilters(timeZone: string) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [reasons, setReasons] = useState<string[]>([]);
  const range = usePersistedDateRange({ key: "dateRange:logbookAudit", defaultLookbackDays: 0 });

  const fromDay = useMemo(() => startOfDay(range.fromDate), [range.fromDate]);
  const toDay = useMemo(() => startOfDay(range.toDate), [range.toDate]);
  const fromDate = useMemo(() => dayBounds(fromDay, timeZone).start, [fromDay, timeZone]);
  const toDate = useMemo(() => dayBounds(toDay, timeZone).end, [toDay, timeZone]);

  const { setRange } = range;
  const setDateRange = useCallback(
    (from?: Date, to?: Date) => {
      const validFrom = from && isValid(from) ? startOfDay(from) : undefined;
      const validTo = to && isValid(to) ? endOfDay(to) : undefined;
      // A start after the current end arrives without an end: write a same-day range in one
      // update instead of leaving an inverted pair for the picker to trip on.
      setRange(validFrom, validTo ?? (validFrom ? endOfDay(validFrom) : undefined));
    },
    [setRange],
  );

  const reset = useCallback(() => {
    setGroups([]);
    setDevices([]);
    setReasons([]);
    const now = new Date();
    setRange(startOfDay(now), endOfDay(now));
  }, [setRange]);

  const today = startOfDay(new Date()).getTime();
  const isDirty =
    groups.length > 0 ||
    devices.length > 0 ||
    reasons.length > 0 ||
    fromDay.getTime() !== today ||
    toDay.getTime() !== today;

  return {
    groups,
    setGroups,
    devices,
    setDevices,
    reasons,
    setReasons,
    /** Picked calendar days (local-midnight Dates) for the picker and file names. */
    fromDay,
    toDay,
    /** Query bounds in the audit's time zone. */
    fromDate,
    toDate,
    setDateRange,
    reset,
    isDirty,
  };
}

export type AuditFilters = ReturnType<typeof useAuditFilters>;
